import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import FormControl from '@mui/material/FormControl';
import Grid from '@mui/material/Grid';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Select from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import DownloadIcon from '@mui/icons-material/Download';

import api from '../../utils/webcall';
import { getErrorMessage } from '../../utils/utils';
import AccessController from '../../components/access_controller';
import { getVizTokens } from '../analytics/vizTokens';
import { EmptyState, StatTile } from '../analytics/components';
import {
    REQUEST_STATUS, dateRange, downloadBlob, money, shortDate, todayIso,
} from './shared';

/**
 * What a staff member sees about themselves.
 *
 * This is the whole app for most of the team, so it answers the three questions
 * they actually open it for, in that order: what have I asked for, what have I
 * been paid, and how much leave have I taken. Their salary is shown because it
 * is theirs; nobody else's is reachable from here at all.
 */

const BLANK_LEAVE = {
    kind: 'leave', start_date: todayIso(), end_date: todayIso(),
    leave_type: 'paid', reason: '',
};
const BLANK_ADVANCE = { kind: 'advance', amount: '', reason: '' };

function RequestDialog(props) {
    const { open, kind, onClose, onSent, showErrorMessage } = props;
    const isMobile = useMediaQuery(useTheme().breakpoints.down('sm'));
    const [form, setForm] = useState(BLANK_LEAVE);
    const [saving, setSaving] = useState(false);
    const [errors, setErrors] = useState({});

    useEffect(() => {
        if (!open) return;
        setErrors({});
        setForm(kind === 'leave' ? BLANK_LEAVE : BLANK_ADVANCE);
    }, [open, kind]);

    const set = (field) => (event) => {
        setForm((prev) => ({ ...prev, [field]: event.target.value }));
        setErrors((prev) => ({ ...prev, [field]: undefined }));
    };

    const validate = () => {
        const found = {};
        if (kind === 'leave') {
            if (!form.start_date) found.start_date = 'Pick the first day';
            if (!form.end_date) found.end_date = 'Pick the last day';
            if (form.start_date && form.end_date && form.end_date < form.start_date) {
                found.end_date = 'The last day cannot be before the first';
            }
        } else {
            const value = Number(form.amount);
            if (!form.amount || Number.isNaN(value) || value <= 0) {
                found.amount = 'Enter how much you need';
            }
        }
        setErrors(found);
        return Object.keys(found).length === 0;
    };

    const submit = () => {
        if (!validate()) return;
        setSaving(true);
        api.post('/staff/me/requests/', form)
            .then(() => onSent())
            .catch((error) => showErrorMessage(getErrorMessage(error)))
            .finally(() => setSaving(false));
    };

    const days = (form.start_date && form.end_date)
        ? Math.round((new Date(form.end_date) - new Date(form.start_date))
            / 86400000) + 1
        : 0;

    return (
        <Dialog open={open} onClose={saving ? undefined : onClose}
            fullWidth maxWidth="xs" fullScreen={isMobile}>
            <DialogTitle>
                {kind === 'leave' ? 'Ask for leave' : 'Ask for an advance'}
            </DialogTitle>
            <DialogContent>
                <Grid container spacing={2} sx={{ mt: 0 }}>
                    {kind === 'leave' ? (
                        <>
                            <Grid size={{ xs: 6 }}>
                                <TextField label="From" type="date" fullWidth
                                    value={form.start_date} onChange={set('start_date')}
                                    InputLabelProps={{ shrink: true }}
                                    error={Boolean(errors.start_date)}
                                    helperText={errors.start_date} />
                            </Grid>
                            <Grid size={{ xs: 6 }}>
                                <TextField label="To" type="date" fullWidth
                                    value={form.end_date} onChange={set('end_date')}
                                    InputLabelProps={{ shrink: true }}
                                    error={Boolean(errors.end_date)}
                                    helperText={errors.end_date} />
                            </Grid>
                            <Grid size={{ xs: 12 }}>
                                <FormControl fullWidth>
                                    <InputLabel id="leave-type">Type of leave</InputLabel>
                                    <Select labelId="leave-type" label="Type of leave"
                                        value={form.leave_type}
                                        onChange={set('leave_type')}>
                                        <MenuItem value="paid">Paid leave</MenuItem>
                                        <MenuItem value="unpaid">Unpaid leave</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                        </>
                    ) : (
                        <Grid size={{ xs: 12 }}>
                            {/* Text, not a number input: a number field cannot be
                                selected by triple-click and changes on a stray
                                scroll wheel, both bad on a money field. */}
                            <TextField label="Amount needed" fullWidth
                                value={form.amount}
                                onChange={(e) => setForm((prev) => ({
                                    ...prev,
                                    amount: e.target.value.replace(/[^\d.]/g, ''),
                                }))}
                                onFocus={(e) => e.target.select()}
                                inputMode="decimal" autoFocus
                                error={Boolean(errors.amount)}
                                helperText={errors.amount} />
                        </Grid>
                    )}
                    {/* Deliberately no figure here. How much can be advanced
                        is the owner's judgement, made when they see the
                        request. Showing a limit - or refusing against one -
                        would let anyone read it back by asking twice. */}
                    {kind === 'advance' && (
                        <Grid size={{ xs: 12 }}>
                            <Alert severity="info">
                                Ask for what you need. An owner will look at it
                                and decide, and may approve part of it.
                            </Alert>
                        </Grid>
                    )}
                    <Grid size={{ xs: 12 }}>
                        <TextField label="Reason" fullWidth multiline minRows={2}
                            value={form.reason} onChange={set('reason')}
                            placeholder={kind === 'leave'
                                ? 'Going home for a wedding'
                                : 'School fees'} />
                    </Grid>
                </Grid>
                {kind === 'leave' && days > 0 && (
                    <Typography variant="caption" sx={{ display: 'block', mt: 1.5 }}>
                        {days} day{days === 1 ? '' : 's'}.
                        {form.leave_type === 'unpaid'
                            ? ' Unpaid leave is deducted from that month\'s pay.'
                            : ' Paid leave does not reduce your pay.'}
                    </Typography>
                )}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={onClose} disabled={saving}>Cancel</Button>
                <Button variant="contained" onClick={submit} disabled={saving}>
                    {saving ? 'Sending...' : 'Send for approval'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

function RequestCard(props) {
    const { request, tokens, onWithdraw } = props;
    const state = REQUEST_STATUS[request.status] || { label: request.status };
    const isLeave = request.kind === 'leave';

    return (
        <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between',
                alignItems: 'flex-start', gap: 1 }}>
                <Box sx={{ minWidth: 0 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        {isLeave
                            ? `${request.days} day${request.days === 1 ? '' : 's'} leave`
                            : `${money(request.amount)} advance`}
                    </Typography>
                    <Typography variant="caption" sx={{ color: tokens.textSecondary }}>
                        {isLeave
                            ? dateRange(request.start_date, request.end_date)
                            : `Asked on ${shortDate(request.created_at?.slice(0, 10))}`}
                    </Typography>
                </Box>
                <Chip size="small" label={state.label} color={state.colour}
                    variant={request.status === 'pending' ? 'outlined' : 'filled'} />
            </Box>

            {request.reason && (
                <Typography variant="body2" sx={{ mt: 1, color: tokens.textSecondary }}>
                    {request.reason}
                </Typography>
            )}

            {/* What was granted, when it differs from what was asked - the case
                worth reading carefully. */}
            {request.status === 'approved' && !isLeave
                && Number(request.granted_amount) !== Number(request.amount) && (
                <Alert severity="info" sx={{ mt: 1, py: 0 }}>
                    {money(request.granted_amount)} approved
                </Alert>
            )}
            {request.status === 'approved' && isLeave
                && request.granted_leave_type && (
                <Typography variant="caption" sx={{ display: 'block', mt: 1 }}>
                    Approved as {request.granted_leave_type} leave
                </Typography>
            )}
            {request.decision_note && (
                <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
                    “{request.decision_note}”
                    {request.decided_by_name ? ` — ${request.decided_by_name}` : ''}
                </Typography>
            )}

            {request.is_open && (
                <Button size="small" sx={{ mt: 1 }} onClick={() => onWithdraw(request)}>
                    Withdraw
                </Button>
            )}
        </Paper>
    );
}

function MyStaffPage(props) {
    const { showErrorMessage, showSuccessMessage } = props;
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const tokens = useMemo(() => getVizTokens(theme.palette.mode === 'dark'),
        [theme.palette.mode]);

    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [notLinked, setNotLinked] = useState('');
    const [dialogKind, setDialogKind] = useState(null);

    const load = useCallback(() => {
        setLoading(true);
        api.get('/staff/me/').then((res) => {
            setData(res.data);
            setNotLinked('');
        }).catch((error) => {
            if (error.response?.status === 404) {
                // Not an error to shout about: the account simply has not been
                // linked to a staff record yet.
                setNotLinked(error.response.data?.detail || 'No staff record found.');
            } else {
                showErrorMessage(getErrorMessage(error));
            }
        }).finally(() => setLoading(false));
    }, [showErrorMessage]);

    useEffect(() => { load(); }, [load]);

    const withdraw = (request) => {
        api.delete(`/staff/me/requests/${request.id}/`).then(() => {
            showSuccessMessage('Request withdrawn');
            load();
        }).catch((error) => showErrorMessage(getErrorMessage(error)));
    };

    const download = (payslip) => {
        api.get(`/staff/payslips/${payslip.id}/document/`, { responseType: 'blob' })
            .then((res) => downloadBlob(res, 'payslip.pdf'))
            .catch(() => showErrorMessage('That payslip could not be downloaded.'));
    };

    const staff = data?.staff;
    const requests = data?.requests || [];
    const pending = requests.filter((r) => r.is_open);
    const decided = requests.filter((r) => !r.is_open);
    const payslips = data?.payslips || [];
    const leave = data?.leave || [];
    const paidLeaveDays = leave
        .filter((record) => record.leave_type === 'paid')
        .reduce((total, record) => total + Number(record.days || 0), 0);

    return (
        <AccessController onlyAdmin={false}>
            <RequestDialog
                open={Boolean(dialogKind)} kind={dialogKind}
                onClose={() => setDialogKind(null)}
                onSent={() => {
                    setDialogKind(null);
                    showSuccessMessage('Sent. An owner will review it.');
                    load();
                }}
                showErrorMessage={showErrorMessage} />

            <Box sx={{ maxWidth: 900, mx: 'auto' }}>
                <Box sx={{ mb: 2 }}>
                    <Typography variant="h5" sx={{ fontWeight: 600 }}>
                        My leave &amp; pay
                    </Typography>
                    <Typography variant="body2" sx={{ color: tokens.textSecondary }}>
                        {staff ? `${staff.name} · ${staff.designation_label}` : ' '}
                    </Typography>
                </Box>

                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                        <CircularProgress />
                    </Box>
                ) : notLinked ? (
                    <Alert severity="info">
                        <AlertTitle>Nothing to show yet</AlertTitle>
                        {notLinked}
                    </Alert>
                ) : (
                    <>
                        <Grid container spacing={2} sx={{ mb: 2 }}>
                            <Grid size={{ xs: 6, md: 4 }}>
                                <StatTile tokens={tokens} label="My salary"
                                    value={money(staff.monthly_salary)}
                                    sub="per month" />
                            </Grid>
                            <Grid size={{ xs: 6, md: 4 }}>
                                <StatTile tokens={tokens} label="Advance to repay"
                                    value={money(data.outstanding_advances)}
                                    sub={Number(data.outstanding_advances) > 0
                                        ? 'taken from your next payslip'
                                        : 'nothing outstanding'}
                                    tone={Number(data.outstanding_advances) > 0
                                        ? 'critical' : undefined} />
                            </Grid>
                            {/* Neither the leave balance nor the advance
                                allowance appears anywhere on this page. Both
                                are the employer's figures, and the API does not
                                send them here at all. */}
                            <Grid size={{ xs: 12, md: 4 }}>
                                <StatTile tokens={tokens} label="Paid leave taken"
                                    value={`${paidLeaveDays} day${paidLeaveDays === 1 ? '' : 's'}`}
                                    sub="recorded so far" />
                            </Grid>
                        </Grid>

                        <Box sx={{ display: 'flex', gap: 1, mb: 3,
                            flexDirection: isMobile ? 'column' : 'row' }}>
                            <Button variant="contained" fullWidth={isMobile}
                                onClick={() => setDialogKind('leave')}>
                                Ask for leave
                            </Button>
                            <Button variant="outlined" fullWidth={isMobile}
                                onClick={() => setDialogKind('advance')}>
                                Ask for an advance
                            </Button>
                        </Box>

                        <Typography variant="overline"
                            sx={{ color: tokens.textSecondary, letterSpacing: '.08em' }}>
                            Waiting for approval
                        </Typography>
                        {pending.length === 0 ? (
                            <Paper variant="outlined" sx={{ p: 2, mt: 0.5, mb: 3 }}>
                                <Typography variant="body2"
                                    sx={{ color: tokens.textSecondary }}>
                                    Nothing waiting. Anything you ask for appears here
                                    until an owner decides.
                                </Typography>
                            </Paper>
                        ) : (
                            <Grid container spacing={1.5} sx={{ mt: 0.25, mb: 3 }}>
                                {pending.map((request) => (
                                    <Grid size={{ xs: 12, sm: 6 }} key={request.id}>
                                        <RequestCard request={request} tokens={tokens}
                                            onWithdraw={withdraw} />
                                    </Grid>
                                ))}
                            </Grid>
                        )}

                        <Typography variant="overline"
                            sx={{ color: tokens.textSecondary, letterSpacing: '.08em' }}>
                            My payslips
                        </Typography>
                        {payslips.length === 0 ? (
                            <Paper variant="outlined" sx={{ mt: 0.5, mb: 3 }}>
                                <EmptyState title="No payslips yet" tokens={tokens}>
                                    Payslips appear here once they have been paid.
                                </EmptyState>
                            </Paper>
                        ) : (
                            <Paper variant="outlined" sx={{ mt: 0.5, mb: 3 }}>
                                {/* The file is locked, and the person has to
                                    know that before they hit a password prompt
                                    in Word and assume it is broken. */}
                                <Alert severity="info" square
                                    sx={{ borderRadius: 0, borderBottom: 1,
                                        borderColor: 'divider' }}>
                                    Payslip files are password protected. Ask an
                                    owner for your password if you do not have it.
                                </Alert>
                                {payslips.map((payslip, index) => (
                                    <React.Fragment key={payslip.id}>
                                        {index > 0 && <Divider />}
                                        {/* The amount sits on its own line on a
                                            phone: squeezed onto one row it left
                                            the payment reference wrapping under
                                            a figure it had nothing to do with. */}
                                        <Box sx={{ display: 'flex', gap: 1, p: 2,
                                            alignItems: isMobile ? 'flex-start' : 'center',
                                            flexWrap: 'wrap' }}>
                                            <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                                                <Box sx={{ display: 'flex', gap: 1,
                                                    alignItems: 'center' }}>
                                                    <Typography variant="subtitle1"
                                                        sx={{ fontWeight: 600 }}>
                                                        {new Date(`${payslip.period_start}T00:00:00`)
                                                            .toLocaleDateString('en-IN',
                                                                { month: 'long', year: 'numeric' })}
                                                    </Typography>
                                                    {payslip.status !== 'paid' && (
                                                        <Chip size="small" label="Not yet paid"
                                                            variant="outlined" color="warning" />
                                                    )}
                                                </Box>
                                                <Typography variant="caption"
                                                    sx={{ color: tokens.textMuted }}>
                                                    {payslip.status === 'paid' ? (
                                                        <>
                                                            Paid {shortDate(payslip.paid_on)}
                                                            {payslip.payment_reference
                                                                ? ` · ${payslip.payment_reference}` : ''}
                                                        </>
                                                    ) : payslip.payday ? (
                                                        <>Payable {shortDate(payslip.payday)}</>
                                                    ) : null}
                                                </Typography>
                                            </Box>
                                            <Typography sx={{ fontWeight: 600,
                                                whiteSpace: 'nowrap',
                                                fontVariantNumeric: 'tabular-nums' }}>
                                                {money(payslip.net_payable)}
                                            </Typography>
                                            <Button size="small" startIcon={<DownloadIcon />}
                                                onClick={() => download(payslip)}
                                                aria-label={`Download payslip for ${payslip.period_start}`}>
                                                Payslip
                                            </Button>
                                        </Box>
                                    </React.Fragment>
                                ))}
                            </Paper>
                        )}

                        {decided.length > 0 && (
                            <>
                                <Typography variant="overline"
                                    sx={{ color: tokens.textSecondary, letterSpacing: '.08em' }}>
                                    Earlier requests
                                </Typography>
                                <Grid container spacing={1.5} sx={{ mt: 0.25 }}>
                                    {decided.map((request) => (
                                        <Grid size={{ xs: 12, sm: 6 }} key={request.id}>
                                            <RequestCard request={request} tokens={tokens}
                                                onWithdraw={withdraw} />
                                        </Grid>
                                    ))}
                                </Grid>
                            </>
                        )}
                    </>
                )}
            </Box>
        </AccessController>
    );
}

export default MyStaffPage;
